import moment from "moment";

// aiim.datasets
import FieldNames from "../../../../../../aiim/datasets/FieldNames";

// aiim.util
import { findField, getAttributeValue } from "../../../../../../aiim/util/aiimUtil";

// components.main.InfoPanel
import { generateShareUrlAsync } from "../../../../InfoPanel/shareUtil";

// context
import Context from "../../../../../../context/Context";
import Topic from "../../../../../../context/Topic";
import { applyGdbVersion } from "../../../../../../spaceplanner/base/queryUtil";

// main.More.Actions.BookWorkspace.WorkspaceReservation
import BookingSystem from "./BookingSystem";
import { getPortalUserByEmail, getUsernameFromPortalUsers } from "../../../../../../spaceplanner/base/ReviewerManagement/reviewersUtil";

const PENDING_APPROVAL = 0;
const APPROVED = 1;
const CANCELED = 3;
const CHECKED_IN = 4;
const CHECKED_OUT = 5;

const ALL_DAY = 1;
const NOT_ALL_DAY = 0;


export default class EsriReservationSchema extends BookingSystem {
  static instance;

  constructor() {
    super();
    this.type = "esri";
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new EsriReservationSchema();
    }
    return this.instance;
  }

  login = () => {
    if (Context.getInstance().user.isAnonymous()) {
      Topic.publish(Topic.SignInClicked, {});
    }
    return Promise.resolve();
  };

  getAccessToken = () => {
    const esriId = Context.instance.lib.esri.esriId;
    const credential = esriId.findCredential(Context.instance.getPortalUrl());
    return Promise.resolve(credential && credential.token);
  };

  getBookingSchedule = (params) => {
    const { checkInDate, checkOutDate, unitId, operation, objectID } = params;
    let ignoreObjectIds = false;
    if (operation && operation === "updateBookingTime") {
      ignoreObjectIds = [objectID]
    }
    return this.checkAvailability([unitId], checkInDate, checkOutDate, ignoreObjectIds, params);
  };

  logout = () => {
    return;
  };

  getLoggedInUser = () => {
    return Context.getInstance().user.portalUser;
  };

  bookWorkspace = (params) => {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    const objectIdField = layer && layer.objectIdField;

    if (!layer) {
      return Promise.reject("No RESERVATIONS layer found");
    }

    let autoCancelUpdates = params.autoCancelUpdates;
    return new Promise(async (resolve, reject) => {
      const { reservations, rejections } = await this._createReservationFeatures(params);

      this._addReservationFeatures(reservations, layer, autoCancelUpdates)
        .then((results) => {
          const addFeatureResults = results.data && results.data.addResults;
          if (addFeatureResults && addFeatureResults[0] && !addFeatureResults[0].success) {
            if (addFeatureResults[0].error && (addFeatureResults[0].error.code === 1000)) {
              console.error(addFeatureResults);
              const i18n = Context.instance.i18n;
              throw new Error(i18n.meetingRooms.book.descriptionLimitError);
            }
          }
          const objectIds = addFeatureResults.map((result) => result.objectId);
          if (objectIds) {
            params.objectIds = objectIds;
            if (objectIds.length > 0) {
              reservations[0].attributes[objectIdField] = objectIds[0];
            }
          } else {
            console.error(addFeatureResults);
            throw new Error("Unable to add reservation(s)");
          }
          return this._verifyUnitAvailability(params, reservations, layer);
        })
        .then(() => {
          resolve({ reservations, rejections, params });
          Topic.publish(Topic.ReservationsUpdated,{});
        }).catch((e) => {
          reject(e);
        });
    });
  };

  updateBooking = async(info)=> {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    const objectIdField = layer.objectIdField;
    const updates = [];
    
    if (info && info.operation === "updateBookingTime") {
      let allDay = 0;
      if (info && info.allDay) allDay = 1;
      const update = { attributes: {} };
      update.attributes[objectIdField] = info.objectId;
      update.attributes[reservations.startTimeField] = (new Date(info.checkIn)).getTime();
      update.attributes[reservations.endTimeField] = (new Date(info.checkOut)).getTime();
      update.attributes[reservations.allDayField] = allDay;
      if (info && info.bookingType === "meetingRooms") update.attributes[reservations.titleField] = info.title;
      updates.push(update);
    }

    const layerUrl = reservations && reservations.url;
    const url = layerUrl+"/applyEdits";
    const params = {
      f: "json",
      useGlobalIds: false,
      rollbackOnFailure: true,
      updates: JSON.stringify(updates)
    };
    const options = {
      query: params,
      method: "post",
      responseType: "json"
    };
    applyGdbVersion(reservations, options.query);

    const lib = Context.getInstance().lib;
    const response = await lib.esri.esriRequest(url,options);
    if (response && response.data.updateResults && response.data.updateResults 
          && response.data.updateResults[0] && response.data.updateResults[0].success) {
        if (info && info.bookingForOther) return response;
        else {
          const objectIdField = layer.objectIdField;
          // create reservation feature for CalendarSelector
          const { reservations, rejections } = await this._createReservationFeatures(info)
          if (reservations && reservations[0] && reservations[0].attributes) {
            reservations[0].attributes[objectIdField] = response.data.updateResults[0].objectId
          }
          return { reservations, rejections };
        }
    }  else {
        throw new Error("Unable to update reservation");
      }
  }

  _addReservationFeatures = (reservations, layer, autoCancelUpdates) => {
    const reservationDataset = Context.instance.aiim.datasets.reservations;
    let layerUrl = reservationDataset.url;
    const url = layerUrl+"/applyEdits";
    const lib = Context.getInstance().lib;
    const options = {
      query: {
        f: "json",
        useGlobalIds: false,
        adds: JSON.stringify(reservations),
        rollbackOnFailure: true
      },
      responseType: "json",
      method: "post"
    };
    if (autoCancelUpdates && autoCancelUpdates.length > 0) {
      options.query.updates = JSON.stringify(autoCancelUpdates);
    }
    applyGdbVersion(reservationDataset, options.query);

    return lib.esri.esriRequest(url,options);
  }

  _verifyUnitAvailability = (params, reservations, layer) => {
    return new Promise((resolve, reject) => {
      const { objectIds } = params;
      const ignoreIds = objectIds.slice();
      if (params.isUpdateBooking) ignoreIds.push(params.reservationObjectId);

      const promises = [];

      reservations.forEach((reservation) => {
        const attrs = reservation.attributes;
        const startTime = getAttributeValue(attrs, FieldNames.START_TIME);
        const endTime = getAttributeValue(attrs, FieldNames.END_TIME);
        const unitId = getAttributeValue(attrs, FieldNames.UNIT_ID);

        const promise = this.checkAvailability([unitId], startTime, endTime, ignoreIds)
          .then((availableUnits) => {
            if (!params.isUpdateBooking) {
              if (availableUnits && availableUnits.length === 1 && availableUnits[0] === unitId) {
                return this._updateReservationFeatures(objectIds, layer);
              } else {
                const err = new Error("Booking lock conflict, aborting reservation...");
                err.code = "booking-lock-conflict";
                throw err;
              }
            } else {
              if (availableUnits && availableUnits.length === 1 && availableUnits[0] === unitId) {
                // na
              } else {
                const err = new Error("Booking lock conflict, aborting reservation update...");
                err.code = "booking-lock-conflict";
                throw err;
              }
            }
          });
        promises.push(promise);
      });

      Promise.all(promises)
        .then(() => resolve())
        .catch((e) => {
          this._deleteConflictingReservations(objectIds, layer)
          reject(e);
        })
    });
  }

  _updateReservationFeatures = (objectIds, layer) => {
    if (!objectIds) {
      return Promise.resolve();
    }

    const reservationDataset = Context.getInstance().aiim.datasets.reservations;
    const layerUrl = reservationDataset && reservationDataset.url;
    const objectIdField = layer.objectIdField;

    const updates = [];

    if (objectIds && objectIds.length > 0)
    objectIds.forEach((objectId) => {
      const update = { attributes: {} };
      update.attributes[objectIdField] = objectId;
      update.attributes[reservationDataset.stateField] = APPROVED;
      updates.push(update);
    });

    const url = layerUrl+"/applyEdits";
    const params = {
      f: "json",
      useGlobalIds: false,
      rollbackOnFailure: true,
      updates: JSON.stringify(updates)
    };
    const options = {
      query: params,
      method: "post",
      responseType: "json"
    };
    applyGdbVersion(reservationDataset, options.query);

    const lib = Context.getInstance().lib;
    return lib.esri.esriRequest(url,options);
  }

  deletePseudoLock = async (objectId) => {
    try {
      await this._deleteConflictingReservations([objectId]);
    } catch (error) {
      console.error("Deleting pseudo lock", error);
    }
  }

  _deleteConflictingReservations = (objectIds, layer) => {
    if (!objectIds) {
      return Promise.resolve();
    }

    const reservationDataset = Context.getInstance().aiim.datasets.reservations;
    const layerUrl = reservationDataset && reservationDataset.url;
    const url = layerUrl+"/applyEdits";
    const params = {
      f: "json",
      useGlobalIds: false,
      rollbackOnFailure: true,
      deletes: objectIds
    };
    const options = {
      query: params,
      method: "post",
      responseType: "json"
    };
    applyGdbVersion(reservationDataset, options.query);

    const lib = Context.getInstance().lib;
    return lib.esri.esriRequest(url,options);
  }

  _createReservationFeatures = async (params) => {
    const reservations = [];
    if (params.others && params.others.length > 0) {
      const rejections = [];
      for (const feature of params.others) {
        const email = getAttributeValue(feature.attributes, FieldNames.PEOPLE_EMAIL);
        try {
          const result = await getPortalUserByEmail({ email });
          const username = getUsernameFromPortalUsers(result, feature);
          const person = {
            username: username,
            fullName: getAttributeValue(feature.attributes, FieldNames.PEOPLE_FULLNAME)
          };
          reservations.push(await this._createReservationFeature({ ...params, person }));
        } catch (e) {
          console.error(e.message);

          // Add people who weren't found in the org to the rejected list
          if (e.message.includes("No result in your organization for user")) {
            rejections.push(feature);
          }
        }
      }

      return { reservations, rejections };
    }

    reservations.push(await this._createReservationFeature(params));
    return { reservations, rejections: null };
  }

  _createReservationFeature = async(params) => {
    const { unit, checkIn, checkOut, allDay, item, person } = params;
    const reservations = Context.instance.aiim.datasets.reservations;
    const unitsDataset = Context.instance.aiim.datasets.units;
    const i18n = Context.instance.i18n;
    const user = this.getLoggedInUser();
    const state = PENDING_APPROVAL;
    const allDayValue = allDay ? ALL_DAY : NOT_ALL_DAY;

    const attributes = {};
    const geometry = unit.geometry;

    // RESERVED_BY_USERNAME, RESERVED_FOR_USERNAME
    if (reservations.reservedByUsernameField) {
      attributes[reservations.reservedByUsernameField] = user.username;
    }
    attributes[reservations.reservedForUsernameField] = person ? person.username : user.username;

    // RESERVED_BY_FULL_NAME, RESERVED_FOR_FULL_NAME
    if (reservations.reservedByFullNameField) {
      attributes[reservations.reservedByFullNameField] = user.fullName;
    }
    attributes[reservations.reservedForFullNameField] = person ? person.fullName : user.fullName;

    // STATE
    attributes[reservations.stateField] = state;

    // START_TIME, END_TIME
    attributes[reservations.startTimeField] = new Date(checkIn).getTime();
    attributes[reservations.endTimeField] = new Date(checkOut).getTime();

    // ALL_DAY
    attributes[reservations.allDayField] = allDayValue;

    // UNIT_ID, UNIT_NAME, LEVEL_ID
    const unitId = getAttributeValue(unit.attributes, FieldNames.UNIT_ID);
    const unitName = getAttributeValue(unit.attributes, FieldNames.NAME);
    const levelData = unitsDataset.getLevelData(unit);
    const levelId = levelData && levelData.levelId;
    if(reservations.unitIdField) attributes[reservations.unitIdField] = unitId;
    if(reservations.unitNameField) attributes[reservations.unitNameField] = unitName;
    if(reservations.levelIdField) attributes[reservations.levelIdField] = levelId;

    // TITLE, DESCRIPTION (optional layer fields)
    const titleField = reservations.titleField;
    const descriptionField = reservations.descriptionField;

    if (titleField && params.title) {
      attributes[titleField] = params.title;
    } else {
      // Populate title with "Booked Workspace - {UNIT_NAME}"
      if (titleField && unitName) {
        const title = i18n.more.bookWorkspace.emailSubject.replace("{workspaceName}", unitName);
        attributes[titleField] = title;
      }
    }

    const bookingType = params && params.bookingType;
    if (descriptionField && item && params.description && (bookingType === "meetingRooms")) {
      attributes[descriptionField] = params.description;
    } else if (!params.noAutoDescription && (bookingType === "meetingRooms")) {
      // Populate description with hyperlink to unit feature
      // if (descriptionField && item) {
      //   const locationUrl = await generateShareUrlAsync(item);
      //   const description = locationUrl;
      //   attributes[descriptionField] = description;
      // }
    }
    return { attributes, geometry };
  }

  getAreaId =(unitId, hotelUnits)=> {
    if (!hotelUnits || hotelUnits.length === 0) return null;
    for (const hotel of hotelUnits){
      const unitIdVal = getAttributeValue(hotel.attributes, FieldNames.UNIT_ID);
      if (unitId === unitIdVal) return getAttributeValue(hotel.attributes, FieldNames.AREA_ID);
    }
  }

  checkAvailability = (unitIds, checkIn, checkOut, ignoreObjectIds, params) => {
    return new Promise((resolve, reject) => {
      const reservations = Context.instance.aiim.datasets.reservations;
      const areaConfigByAreaId = params && params.areaConfigByAreaId;
      const hotelUnits = params && params.hotelUnits;
      // if(!reservations) return;
      const layer = reservations && reservations.layer2D;

      const ids = unitIds.map((unitId) => `'${unitId}'`);
      const whereClauses = [
        `${reservations.unitIdField} IN (${ids.join(", ")})`
      ];
      // Ignore the specified objectId
      // ignore ongoing booking id
      if (ignoreObjectIds && layer) {
        whereClauses.push(`${layer.objectIdField} NOT IN (${ignoreObjectIds.join(",")})`);
      }
      const where = `${whereClauses.join(" AND ")}`;

      const availableUnits = [];
      reservations.query(where)
        .then(async(data) => {
          const features = data.features;
          const reservedUnits = {};
          features.forEach((reservation) => {
            const unitId = getAttributeValue(reservation.attributes, FieldNames.UNIT_ID);
            if (!reservedUnits[unitId]) {
              reservedUnits[unitId] = [reservation];
            } else {
              reservedUnits[unitId].push(reservation);
            }
          });

          for (const unitId of unitIds) {
            if (reservedUnits[unitId]) {
              const unitReservations = reservedUnits[unitId];
              const areaId = this.getAreaId(unitId, hotelUnits);
              const autoCancelLimits = areaConfigByAreaId && areaConfigByAreaId[areaId];
              const info = this._canReserve(unitReservations, checkIn, checkOut, autoCancelLimits);
              if (info.canReserve) {
                availableUnits.push(unitId);
              }
              if (params && params.task) {
                params.task.canReserveInfo = info;
              }
            } else {
              availableUnits.push(unitId);
            }
          };
        })
        .then(()=> {
          resolve(availableUnits);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  _canReserve(unitReservations, checkIn, checkOut, cancelLimits) {

    const autoCancelLimits = {
      regular: cancelLimits && cancelLimits.autoCancelMinutes,
      allDay: cancelLimits && cancelLimits.autoCancelMinutesAllDay
    }

    const currentDateTime = new Date().getTime();
    let canReserve = true;
    const unitId = getAttributeValue(unitReservations[0].attributes, FieldNames.UNIT_ID);
    let objectIdsToCancel = [];
    const reservations = Context.getInstance().aiim.datasets && Context.getInstance().aiim.datasets.reservations;

    unitReservations.forEach((unitReservation) => {
      const attrs = unitReservation.attributes;
      const currentTime = new Date().getTime();
      const reservationState = getAttributeValue(attrs, FieldNames.STATE);
      const reservationStart = getAttributeValue(attrs, FieldNames.START_TIME);
      const reservationEnd = getAttributeValue(attrs, FieldNames.END_TIME);
      const reservationObjId = getAttributeValue(attrs, reservations.getObjectIdField())
      const reservationAllDay = getAttributeValue(attrs, FieldNames.ALL_DAY) === 1;
      let autoCancelMins;

      const mReservationStart = moment(reservationStart);
      const mReservationEnd = moment(reservationEnd);
      const mCheckIn = moment(checkIn);
      const mCheckOut = moment(checkOut);

      const activeReservation =
        reservationState === PENDING_APPROVAL ||
        reservationState === APPROVED ||
        reservationState === CHECKED_IN;
      const notAvailable =
        (mReservationStart < mCheckIn && mReservationEnd > mCheckIn) ||
        (mReservationStart >= mCheckIn && mCheckOut > mReservationStart);
      const pastBooking = reservationEnd <= currentTime;

      let hasAutoCancelMins = false;
      if(reservationAllDay) autoCancelMins = autoCancelLimits && autoCancelLimits.allDay;
      else autoCancelMins = autoCancelLimits && autoCancelLimits.regular;

      if (typeof autoCancelMins === 'number' && (autoCancelMins >= 1)) {
        hasAutoCancelMins = true;
      }
      let canAutoCancel = false;

      if (activeReservation && notAvailable && hasAutoCancelMins) {
        let stateOk = reservationState === PENDING_APPROVAL || reservationState === APPROVED;
        let withinBooking = (reservationStart < currentDateTime) && (currentDateTime < reservationEnd);
        const elapsed = currentDateTime - reservationStart;
        const max = autoCancelMins * 60000;
        canAutoCancel = stateOk && withinBooking && (elapsed > max);
      }

      if (activeReservation && notAvailable && !pastBooking) {
        if (!canAutoCancel) {
          canReserve = false;
        } else {
          objectIdsToCancel.push(reservationObjId);
        }
      }
    });

    if (canReserve && objectIdsToCancel && objectIdsToCancel.length > 1) {
      canReserve = false;
    }

    return {
      unitId : unitId,
      canReserve : canReserve,
      objectIdsToCancel: objectIdsToCancel
    }
  }

  cancelBooking = (attributes) => {
    const reservationDataset = Context.instance.aiim.datasets.reservations;
    const layer = reservationDataset && reservationDataset.layer2D;
    const layerUrl = reservationDataset && reservationDataset.url;
    if (!layer) {
      return Promise.resolve();
    }

    const objectIdField = layer.objectIdField;
    const updateFeatures = [];

    const addUpdate = (attrs) => {
      const objectId = attrs[objectIdField];
      const update = { attributes: {} };
      update.attributes[objectIdField] = objectId;
      update.attributes[reservationDataset.stateField] = CANCELED;
      updateFeatures.push(update);
    }

    if (Array.isArray(attributes)) {
      attributes.forEach((attrs) => addUpdate(attrs));
    } else {
      addUpdate(attributes);
    }

    const url = layerUrl+"/applyEdits";
    const params = {
      f: "json",
      useGlobalIds: false,
      rollbackOnFailure: true,
      updates: JSON.stringify(updateFeatures)
    };
    const options = {
      query: params,
      method: "post",
      responseType: "json"
    };
    applyGdbVersion(reservationDataset, options.query);

    const lib = Context.getInstance().lib;
    return lib.esri.esriRequest(url,options);
  }

  queryCheckInOut =(uniqueId)=> {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    let objectId;
    if(layer && layer.objectIdField) objectId = layer.objectIdField;
    let url = layer.url;
    let layerId = layer.layerId;
    url = url + "/" + layerId
    url = Context.checkMixedContent(url);
    const lib = Context.getInstance().lib;
    const task = new lib.esri.QueryTask({url: url});
    const query = new lib.esri.Query();
    query.outFields = ["*"];
    query.returnGeometry = true;
    query.returnZ = true;
    query.where = objectId + " = " + uniqueId;
    return task.execute(query);
  }


  updateCheckInOut =(attributes, stateVal)=> {
    const reservationDataset = Context.instance.aiim.datasets.reservations;
    const layer = reservationDataset && reservationDataset.layer2D;
    const layerUrl = reservationDataset && reservationDataset.url;
    const fields = layer && layer.fields;
    let objectId;
    if(layer && layer.objectIdField) objectId = layer.objectIdField;
    const checkInTime = findField(fields, FieldNames.CHECK_IN_TIME).name;
    const checkOutTime = findField(fields, FieldNames.CHECK_OUT_TIME).name;
    const state = findField(fields, FieldNames.STATE).name;

    const checkInTimeVal = getAttributeValue(attributes, FieldNames.CHECK_IN_TIME);
    const objectIdVal = getAttributeValue(attributes,objectId);
    const checkOutTimeVal = getAttributeValue(attributes, FieldNames.CHECK_OUT_TIME);

    const update = { attributes: {} };
    update.attributes[objectId] = objectIdVal;
    update.attributes[checkInTime] = checkInTimeVal;
    update.attributes[checkOutTime] = checkOutTimeVal;
    if(stateVal === "checkIn") update.attributes[state] = CHECKED_IN;
    else if(stateVal === "checkOut") update.attributes[state] = CHECKED_OUT;

    const url = layerUrl+"/applyEdits";
    const params = {
      f: "json",
      useGlobalIds: false,
      rollbackOnFailure: true,
      updates: JSON.stringify([update])
    };
    const options = {
      query: params,
      method: "post",
      responseType: "json"
    };
    applyGdbVersion(reservationDataset, options.query);

    const lib = Context.getInstance().lib;
    return lib.esri.esriRequest(url,options);
  }
}
